/**
 * "Your Year" — what actually matters, per grade, per season.
 *
 * Why this exists (founder, 2026-08-04): "a freshman can extract a ton of
 * value by planning for 4 years — explore interests, activities, summer
 * programs, plan classes.. so we have to have a strategy for each year."
 *
 * The gap this closes: onboarding routes by grade (WelcomePage), and then
 * that awareness EVAPORATES. A freshman returning on day two saw a senior's
 * product — add colleges, read your odds — which is both wrong for them and
 * spends their free credits on a question four years early. Retention past
 * day one is the binding constraint, and this is why.
 *
 * Every move here points at a feature that ALREADY EXISTS. Nothing new is
 * promised; the underclassman value was always in the product (Major Map,
 * Activity Ideas, Summer Programs, the 12-phase roadmap) and only ever
 * framed for seniors.
 *
 * Season matters as much as grade: "make this summer count" is useless in
 * May and urgent in October. Phases mirror milestones.py — Fall (Aug–Dec),
 * Spring (Jan–May), Summer (Jun–Jul).
 */

export const SEASONS = { FALL: 'fall', SPRING: 'spring', SUMMER: 'summer' };

export function seasonOf(now = new Date()) {
    const m = now.getMonth() + 1;              // 1–12
    if (m >= 8) return SEASONS.FALL;           // Aug–Dec
    if (m >= 6) return SEASONS.SUMMER;         // Jun–Jul
    return SEASONS.SPRING;                     // Jan–May
}

/**
 * Each move: `key` (done-detection), `title`, `why` (why THIS year — the
 * sentence that earns the click), `to`, and optional `seasons` limiting when
 * it surfaces. `free` marks moves that cost nothing, so an underclassman is
 * never pushed at a paid action before the product has proven itself.
 */
export const YEAR_PLANS = {
    9: {
        label: 'Freshman year',
        headline: 'The four-year head start',
        thesis: 'Colleges read four years of record. Most families start junior year — '
            + 'you have three more than they do, and that advantage compounds.',
        moves: [
            {
                key: 'major_map', title: 'Map your majors', to: '/major-map', free: true,
                why: 'Direction first. Nothing is locked in — this is the map you will refine '
                    + 'for four years, and it decides which classes and activities are worth your time.',
            },
            {
                key: 'activities', title: 'Get activity ideas', to: '/activities', free: true,
                why: 'The activities you start now are the ones that become leadership by junior '
                    + 'year. Depth is what colleges read, and depth takes years — not summers.',
            },
            {
                key: 'summer', title: 'Find a summer that counts', to: '/programs',
                seasons: [SEASONS.FALL, SEASONS.SPRING],
                why: 'Freshman summer is the cheapest time to try something and find out you hate it. '
                    + 'Explore widely now so you can specialize later with conviction.',
            },
            {
                key: 'roadmap', title: 'See how the four years build', to: '/roadmap',
                why: 'Course rigor compounds: what you take this year decides what you are allowed '
                    + 'to take senior year. Seeing the whole ladder now is the point.',
            },
        ],
        pitch: 'Four years of compounding beats four months of scrambling. Everything you build '
            + 'from here lands in the application you send in 2029.',
    },
    10: {
        label: 'Sophomore year',
        headline: 'Build the foundation',
        thesis: 'The record colleges will read is being written right now — sophomore grades, '
            + 'sophomore rigor, and the activities you are about to go deep on.',
        moves: [
            {
                key: 'major_map', title: 'Sharpen your direction', to: '/major-map', free: true,
                why: 'Freshman year was for exploring. This year narrows it: which of these '
                    + 'directions actually fits your record, and what would make it stronger?',
            },
            {
                key: 'activities', title: 'Go deeper, not wider', to: '/activities', free: true,
                why: 'Colleges read trajectory, not inventory. Two activities with real depth '
                    + 'beat eight with none — and sophomore year is when depth starts being possible.',
            },
            {
                key: 'summer', title: 'Make this summer matter', to: '/programs',
                seasons: [SEASONS.FALL, SEASONS.SPRING],
                why: 'Sophomore summer is when programs start being selective — and start counting. '
                    + 'Applications for the good ones close months before the summer starts.',
            },
            {
                key: 'colleges', title: 'Take an early look at colleges', to: '/universities',
                why: 'No list, no pressure — just calibration. Knowing where you stand today tells '
                    + 'you exactly what the next two years need to build.',
            },
        ],
        pitch: 'Two years of runway is still an enormous advantage. Use it to build a record, '
            + 'not to scramble for one.',
    },
    11: {
        label: 'Junior year',
        headline: 'The pivotal year',
        thesis: 'This is the year that decides the application. Every grade, every activity and '
            + 'every test score from here lands in next fall’s file.',
        moves: [
            {
                key: 'colleges', title: 'Build your college list', to: '/universities',
                why: 'This is the year the list becomes real. Start from the schools that already '
                    + 'fit your record — then stretch deliberately, not hopefully.',
            },
            {
                key: 'fits', title: 'Know your odds honestly', to: '/universities',
                why: 'Reaches, targets and safeties grounded in real reported decisions from '
                    + 'students with your profile — not in optimism.',
            },
            {
                key: 'summer', title: 'The summer that matters most', to: '/programs',
                seasons: [SEASONS.FALL, SEASONS.SPRING],
                why: 'Junior summer is the last one colleges will see. It is also the one with the '
                    + 'earliest deadlines — many close in January and February.',
            },
            {
                key: 'essays', title: 'Start thinking about essays', to: '/roadmap?filter=essays',
                seasons: [SEASONS.SPRING, SEASONS.SUMMER],
                why: 'Students who write well in the fall started thinking in the spring. The hard '
                    + 'part is not writing — it is knowing what your story actually is.',
            },
        ],
        pitch: 'Junior year is where a plan stops being optional. Everything compounds into next fall.',
    },
    12: {
        label: 'Senior year',
        headline: 'Execute',
        thesis: 'The record is written. What is left is judgment, deadlines, and finishing well.',
        moves: [
            {
                key: 'colleges', title: 'Lock your list', to: '/universities',
                why: 'Balanced, not hopeful — some reaches, some real targets, safeties you would '
                    + 'genuinely be glad to attend.',
            },
            {
                key: 'fits', title: 'Read your odds at every school', to: '/universities',
                why: 'Before you commit an application fee and an essay, know honestly where you stand.',
            },
            {
                key: 'essays', title: 'Work your essays', to: '/roadmap?filter=essays',
                why: 'Every school on your list, every supplement, one workspace — with your record '
                    + 'already in context.',
            },
            {
                key: 'roadmap', title: 'Every deadline in one place', to: '/roadmap',
                why: 'Early deadlines land in weeks, not months. One timeline, anchored to your '
                    + 'graduation, so nothing is discovered late.',
            },
        ],
        pitch: 'One season left. The difference now is organization and honesty about the odds.',
    },
};

/** The moves that apply to this grade RIGHT NOW (season-filtered). */
export function movesFor(gradeNum, now = new Date()) {
    const plan = YEAR_PLANS[gradeNum];
    if (!plan) return [];
    const season = seasonOf(now);
    return plan.moves.filter((m) => !m.seasons || m.seasons.includes(season));
}
